import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core';
import { ArrowBack, Email, GetApp, OpenInNew, PlayArrow, Refresh } from '@material-ui/icons';
import { toast } from 'react-toastify';

import {
  getBatch,
  getBatchStatus,
  processBatch,
  generateStudent,
  exportBatch,
  sendEmails,
} from '../../../../../services/certificateBatches';
import AdminMenu from '../../../../../components/Admin/MenuDetalhes/AdminMenu';
import {
  MainContainer,
  ContentContainer,
  Title,
  StatusBadge,
  StyledTableContainer,
  EmptyState,
} from '../styles';
import {
  BackLink,
  HeaderRow,
  CompanyLogo,
  TitleBlock,
  ConfigLine,
  ColorSwatch,
  EditNotice,
  ActionsRow,
  StatsRow,
  StatCard,
  ProgressWrapper,
  ProgressLabel,
  ErrorMessage,
  EmailSentHint,
  CertLinkWrapper,
  LoadingWrapper,
  LoadErrorWrapper,
} from './styles';

const POLL_INTERVAL_MS = 4000;
const MAX_POLL_FAILURES = 3;

const BATCH_STATUS_LABEL = {
  draft: 'Rascunho',
  processing: 'Processando…',
  done: 'Concluída',
  partial_error: 'Com erros',
};

const BATCH_STATUS_STYLE = {
  draft: { backgroundColor: '#eee', color: '#616161' },
  processing: { backgroundColor: '#FFF3E0', color: '#E65100' },
  done: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  partial_error: { backgroundColor: '#FFEBEE', color: '#C62828' },
};

const STUDENT_STATUS = {
  pending: { label: 'Pendente', style: { backgroundColor: '#eee', color: '#616161' } },
  registered: { label: 'Cadastrado', style: { backgroundColor: '#E3F2FD', color: '#1565C0' } },
  generated: { label: 'Gerado', style: { backgroundColor: '#E8F5E9', color: '#2E7D32' } },
  error: { label: 'Erro', style: { backgroundColor: '#FFEBEE', color: '#C62828' } },
};

// Tela final da turma: dispara o processamento em lote, permite retry por
// aluno, exporta a planilha e envia os certificados por e-mail. Não existe
// edição pós-criação nesta fase (o wizard é só criação) — o header mostra a
// config resumida sem botão de editar; isso fica registrado como limitação
// conhecida pro backlog.
export default function BatchDetail() {
  const { id } = useParams();
  const history = useHistory();

  const [batch, setBatch] = useState(null);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const [isProcessStarting, setIsProcessStarting] = useState(false);
  const [isSampling, setIsSampling] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [rowLoadingId, setRowLoadingId] = useState(null);

  const [isEmailDialogOpen, setIsEmailDialogOpen] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false);

  const pollRef = useRef(null);
  const pollFailuresRef = useRef(0);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const refreshStatus = useCallback(async () => {
    try {
      const { data } = await getBatchStatus(id);
      setStatus(data);
      pollFailuresRef.current = 0;
      if (data.status !== 'processing') stopPolling();
      return data;
    } catch (error) {
      pollFailuresRef.current += 1;
      if (pollFailuresRef.current >= MAX_POLL_FAILURES) {
        stopPolling();
        toast.error('Não foi possível atualizar o status — recarregue a página.');
      }
      return null;
    }
  }, [id, stopPolling]);

  const startPolling = useCallback(() => {
    if (!pollRef.current) {
      pollRef.current = setInterval(refreshStatus, POLL_INTERVAL_MS);
    }
  }, [refreshStatus]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setIsLoading(true);
      setLoadError(false);
      const [batchResult, statusData] = await Promise.all([
        getBatch(id).catch(() => {
          toast.error('Não foi possível carregar a turma.');
          return null;
        }),
        refreshStatus(),
      ]);
      if (!isMounted) return;
      if (batchResult) setBatch(batchResult.data);
      if (!batchResult || !statusData) {
        setLoadError(true);
        setIsLoading(false);
        return;
      }
      // Se a aba foi reaberta com a turma ainda processando no backend,
      // retoma o polling automaticamente em vez de deixar a tela parada.
      if (statusData.status === 'processing') startPolling();
      setIsLoading(false);
    })();

    return () => {
      isMounted = false;
      stopPolling();
    };
  }, [id, refreshStatus, startPolling, stopPolling]);

  const isBatchProcessing = status?.status === 'processing';

  const handleProcess = async () => {
    if (isProcessStarting || isBatchProcessing) return;
    setIsProcessStarting(true);
    try {
      await processBatch(id);
    } catch (error) {
      if (error?.response?.status === 409) {
        toast.info('Essa turma já está sendo processada — acompanhando o progresso.');
      } else {
        toast.error('Não foi possível iniciar o processamento da turma.');
        setIsProcessStarting(false);
        return;
      }
    }
    const data = await refreshStatus();
    if (data?.status === 'processing') startPolling();
    setIsProcessStarting(false);
  };

  const handleSample = async () => {
    const first = status?.students?.[0];
    if (!first) return;
    setIsSampling(true);
    try {
      await generateStudent(id, first.id);
      const data = await refreshStatus();
      const updated = data?.students?.find((s) => s.id === first.id);
      if (updated?.status === 'error') {
        toast.error(updated.error_message || `Não foi possível gerar o certificado de ${first.nome}.`);
      } else if (updated?.certificate_url) {
        const popup = window.open(updated.certificate_url, '_blank', 'noopener');
        if (!popup) {
          toast.info('O navegador bloqueou a nova aba — use o link "Abrir PDF" na linha do aluno.');
        } else {
          toast.success(`Amostra gerada para ${first.nome}.`);
        }
      } else {
        toast.success('Amostra gerada — use o link "Abrir PDF" na linha do aluno.');
      }
    } catch (error) {
      toast.error(`Não foi possível gerar o certificado de ${first.nome}.`);
    } finally {
      setIsSampling(false);
    }
  };

  const handleGenerateStudent = async (student) => {
    setRowLoadingId(student.id);
    try {
      await generateStudent(id, student.id);
      await refreshStatus();
    } catch (error) {
      toast.error(`Não foi possível gerar o certificado de ${student.nome}.`);
    } finally {
      setRowLoadingId(null);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportBatch(id, batch?.title);
    } catch (error) {
      toast.error('Não foi possível exportar a planilha.');
    } finally {
      setIsExporting(false);
    }
  };

  const eligibleForEmail = (status?.students || []).filter(
    (s) => s.status === 'generated' && !s.email_sent_at
  );

  const handleConfirmSendEmails = async () => {
    setIsSendingEmails(true);
    try {
      const { data } = await sendEmails(
        id,
        eligibleForEmail.map((s) => s.id)
      );
      toast.success(
        `E-mails enviados: ${data.sent} · falharam: ${data.failed} · ignorados: ${data.skipped}`
      );
      await refreshStatus();
      setIsEmailDialogOpen(false);
    } catch (error) {
      toast.error('Não foi possível enviar os e-mails.');
    } finally {
      setIsSendingEmails(false);
    }
  };

  if (loadError) {
    return (
      <MainContainer>
        <AdminMenu />
        <ContentContainer>
          <LoadErrorWrapper>
            <p>Turma não encontrada ou indisponível.</p>
            <BackLink type="button" onClick={() => history.push('/admin/certificados/turmas')}>
              <ArrowBack fontSize="small" /> Voltar pra lista
            </BackLink>
          </LoadErrorWrapper>
        </ContentContainer>
      </MainContainer>
    );
  }

  if (isLoading || !batch || !status) {
    return (
      <MainContainer>
        <AdminMenu />
        <ContentContainer>
          <LoadingWrapper>Carregando…</LoadingWrapper>
        </ContentContainer>
      </MainContainer>
    );
  }

  const { counts, students } = status;
  const total = students.length;
  const processed = counts.generated + counts.error;
  const signaturesCount = Array.isArray(batch.signatures) ? batch.signatures.length : batch.signatures;

  return (
    <MainContainer>
      <AdminMenu />
      <ContentContainer>
        <BackLink type="button" onClick={() => history.push('/admin/certificados/turmas')}>
          <ArrowBack fontSize="small" /> Turmas
        </BackLink>

        <HeaderRow>
          {batch.company?.logo_url && <CompanyLogo src={batch.company.logo_url} alt={batch.company?.name || ''} />}
          <TitleBlock>
            <Title>{batch.title}</Title>
            <ConfigLine>
              <span>{batch.course_name}</span>
              {batch.training_days && <span>· {batch.training_days}</span>}
              {batch.workload_hours && <span>· {batch.workload_hours}h</span>}
              {batch.company?.color && (
                <span>
                  · <ColorSwatch color={batch.company.color} /> {batch.company.color}
                </span>
              )}
              {!!signaturesCount && <span>· {signaturesCount} assinatura{signaturesCount === 1 ? '' : 's'}</span>}
              <StatusBadge
                statusColor={BATCH_STATUS_STYLE[status.status]?.color}
                statusBg={BATCH_STATUS_STYLE[status.status]?.backgroundColor}
              >
                {BATCH_STATUS_LABEL[status.status] || status.status}
              </StatusBadge>
            </ConfigLine>
            <EditNotice>Edição da turma indisponível nesta fase.</EditNotice>
          </TitleBlock>
        </HeaderRow>

        <StatsRow>
          <StatCard bg="#eee" color="#616161">
            <strong>{counts.pending}</strong>
            Pendentes
          </StatCard>
          <StatCard bg="#E3F2FD" color="#1565C0">
            <strong>{counts.registered}</strong>
            Cadastrados
          </StatCard>
          <StatCard bg="#E8F5E9" color="#2E7D32">
            <strong>{counts.generated}</strong>
            Gerados
          </StatCard>
          <StatCard bg="#FFEBEE" color="#C62828">
            <strong>{counts.error}</strong>
            Com erro
          </StatCard>
        </StatsRow>

        <ActionsRow>
          <Button
            type="button"
            variant="outlined"
            startIcon={isSampling ? <CircularProgress size={16} /> : <OpenInNew />}
            disabled={isBatchProcessing || isSampling || total === 0}
            onClick={handleSample}
          >
            Gerar amostra (1º aluno)
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            startIcon={
              isBatchProcessing || isProcessStarting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <PlayArrow />
              )
            }
            disabled={isBatchProcessing || isProcessStarting || total === 0}
            onClick={handleProcess}
          >
            {isBatchProcessing ? `Processando… ${processed}/${total}` : 'Gerar todos'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            startIcon={isExporting ? <CircularProgress size={16} /> : <GetApp />}
            disabled={isExporting}
            onClick={handleExport}
          >
            Exportar planilha
          </Button>
          <Button
            type="button"
            variant="outlined"
            startIcon={<Email />}
            disabled={isBatchProcessing || eligibleForEmail.length === 0}
            onClick={() => setIsEmailDialogOpen(true)}
          >
            Enviar por e-mail
          </Button>
        </ActionsRow>

        {isBatchProcessing && (
          <ProgressWrapper>
            <ProgressLabel>Processando certificados… {processed}/{total}</ProgressLabel>
            <LinearProgress variant="determinate" value={total ? (processed / total) * 100 : 0} />
          </ProgressWrapper>
        )}

        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Aluno</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Certificado</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {total === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <EmptyState>Nenhum aluno cadastrado nessa turma.</EmptyState>
                  </TableCell>
                </TableRow>
              )}

              {students.map((s) => {
                const config = STUDENT_STATUS[s.status] || { label: s.status, style: {} };
                const isRowLoading = rowLoadingId === s.id;

                return (
                  <TableRow key={s.id}>
                    <TableCell>{s.nome}</TableCell>
                    <TableCell>
                      <StatusBadge statusColor={config.style.color} statusBg={config.style.backgroundColor}>
                        {config.label}
                      </StatusBadge>
                      {s.email_sent_at && (
                        <EmailSentHint title={new Date(s.email_sent_at).toLocaleString('pt-BR')}>
                          <Email fontSize="inherit" /> enviado
                        </EmailSentHint>
                      )}
                      {s.error_message && <ErrorMessage>{s.error_message}</ErrorMessage>}
                    </TableCell>
                    <TableCell>
                      {s.certificate_url && (
                        <CertLinkWrapper>
                          <a href={s.certificate_url} target="_blank" rel="noreferrer">
                            Abrir PDF
                          </a>
                        </CertLinkWrapper>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        title={s.status === 'generated' ? 'Regerar certificado' : 'Gerar certificado'}
                        size="small"
                        disabled={isBatchProcessing || rowLoadingId !== null}
                        onClick={() => handleGenerateStudent(s)}
                      >
                        {isRowLoading ? <CircularProgress size={18} /> : <Refresh fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </ContentContainer>

      <Dialog open={isEmailDialogOpen} onClose={() => !isSendingEmails && setIsEmailDialogOpen(false)}>
        <DialogTitle>Enviar certificados por e-mail</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Enviar certificado por e-mail para {eligibleForEmail.length} aluno
            {eligibleForEmail.length === 1 ? '' : 's'}?
          </DialogContentText>
        </DialogContent>
        <DialogActions style={{ padding: '0 1.5rem 1.5rem' }}>
          <Button variant="text" color="secondary" disabled={isSendingEmails} onClick={() => setIsEmailDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={isSendingEmails}
            startIcon={isSendingEmails ? <CircularProgress size={16} color="inherit" /> : null}
            onClick={handleConfirmSendEmails}
          >
            {isSendingEmails ? 'Enviando…' : 'Enviar'}
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
}
